'use client'

import { MoreVertIcon } from '@repo/ui/MoreVertIcon'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMenuState, MenuItem } from '@szhsin/react-menu'
import { useNavigate } from 'react-router-dom'
import { useRef } from 'react'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { useThirdPartyFormWizardStore } from '@repo/store/useThirdPartyFormWizardStore'
import IconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'

const FORMEVO_DISPLAY_URL = 'https://www.formevo.co.uk/myforms.html?action=form_display&_id='

type MoreMenuCardProps = {
  rowData?: any
}

export const MoreMenuCard = ({ rowData }: MoreMenuCardProps) => {
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const ref = useRef(null)
  const navigate = useNavigate()
  const { fetchDocumentContent, addDocumentToMatterHistory } = useThirdPartyFormsStore()
  const { initialize } = useThirdPartyFormWizardStore()
  const { currentMatter } = useMattersStore()

  const matterDocumentId = rowData?.matterDocumentId
  const providerDocumentId = rowData?.providerDocumentId

  const handleUpdateForm = () => {
    initialize({ mode: 'edit', selectedMatter: currentMatter, formData: rowData })
    navigate('?wizard=ThirdPartyForm&stage=add-form&step=select-options')
    toggleMenu(false)
  }

  const handlePreviewPdf = async () => {
    toggleMenu(false)
    if (!matterDocumentId) return

    // Open the tab synchronously so it isn't blocked by popup blockers, then
    // point it at the document once the (debounced/async) fetch resolves.
    const previewWindow = window.open('', '_blank')

    try {
      const blob = await fetchDocumentContent({ matterDocumentId })
      if (!blob) {
        previewWindow?.close()
        return
      }
      const url = URL.createObjectURL(blob)
      if (previewWindow) {
        previewWindow.location.href = url
      } else {
        // Popup was blocked - fall back to a direct open.
        window.open(url, '_blank', 'noopener,noreferrer')
      }
      // Give the tab time to load before releasing the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      previewWindow?.close()
      // error toast handled by ospreyAPI
    }
  }

  const handleImportPdf = async () => {
    toggleMenu(false)
    if (!matterDocumentId) return

    try {
      await addDocumentToMatterHistory({ matterDocumentId })
    } catch {
      // error toast handled by the store
    }
  }

  const handleOpenInFormEvo = () => {
    if (providerDocumentId) {
      window.open(`${FORMEVO_DISPLAY_URL}${providerDocumentId}`, '_blank', 'noopener,noreferrer')
    }
    toggleMenu(false)
  }

  return (
    <MUIStack>
      <IconButton
        data-testid="ThirdPartyFormsMoreMenu"
        size="small"
        aria-label="More actions"
        onClick={() => toggleMenu(true)}
        ref={ref}
      >
        <MoreVertIcon sx={{ height: '20px', width: '20px' }} />
      </IconButton>

      <PopupWithBackdrop menuState={menuState} anchorRef={ref} toggleMenu={toggleMenu} className="masterActionMenu">
        <MenuItem onClick={handleUpdateForm}>Update form</MenuItem>
        <MenuItem onClick={handlePreviewPdf} disabled={!matterDocumentId}>
          Preview PDF
        </MenuItem>
        <MenuItem onClick={handleImportPdf} disabled={!matterDocumentId}>
          Import PDF to Matter History
        </MenuItem>
        <MenuItem onClick={handleOpenInFormEvo} disabled={!providerDocumentId}>
          Open in FormEvo
        </MenuItem>
      </PopupWithBackdrop>
    </MUIStack>
  )
}
